import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRowParams,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import { NoAssignees } from "./StreamTableRow";
import { useNavigate } from "react-router-dom";
import { User } from "../../../../users-admin/types/User";
import { TableRowCommentsCounts } from "./TableRowCommentsCount";
import { LastUpdatedByAvatar } from "./LastUpdatedByAvatar";
import { TableRowAssignees } from "./TableRowAssignees";
import { CanvaLinkButton } from "./CanvaLinkButton";
import { MemoryBookStatusChip } from "./MemoryBookStatusChip";
import { useEffect, useState } from "react";
import { DownloadIcon } from "lucide-react";
import { DownloadGraphicModal } from "./DownloadGraphicModal";
import memorygraphicpattern from "../../../../../assets/thankyoupattern.png";
import muzukurulogo from "../../../../../assets/muzukurublack.png";
import PropTypes from "prop-types";
import { TableRowScannedComments } from "./TableRowScannedComments";
import { AssignMemoryBookModal } from "./AssignMemoryBookModal";
import { tokens } from "../../../../../theme";
import { Assignment } from "@mui/icons-material";
import { MobileView } from "./MobileView";

interface MemoryBookStreamInfo {
  is_delivered: boolean;
  is_memory_book_available: boolean;
  approved_comments: number;
  total_comments: number;
  canva_memory_book_link: string | null;
  assignees: User[];
  created_at: string;
  last_updated_by: User;
  name_of_deceased: string;
  stream_date: string;
  stream_id: number;
  updated_at: string;
  physical_comments?: number;
  done_comments?: number;
  due_date?: string;
  booking_service?: string | null;
}

interface Props {
  tableViewStreams: MemoryBookStreamInfo[];
  isLoading: boolean;
  paginationModel: GridPaginationModel;
  rowCount: number;
  handlePaginationModelChange: (newModel: GridPaginationModel) => void;
  onAssignSuccess: () => void;
}

const TABLET_HIDDEN_COLUMNS: GridColumnVisibilityModel = {
  stream_id: false,
  comments: false,
  scanned_comments: false,
  last_updated_by: false,
  canva_link: false,
};

export const TableView: React.FC<Props> = ({
  tableViewStreams,
  isLoading,
  paginationModel,
  rowCount,
  handlePaginationModelChange,
  onAssignSuccess,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(isTablet ? TABLET_HIDDEN_COLUMNS : {});

  useEffect(() => {
    setColumnVisibilityModel(isTablet ? TABLET_HIDDEN_COLUMNS : {});
  }, [isTablet]);

  const goToStream = (row: MemoryBookStreamInfo) => {
    navigate(`/memory-table/view/${row.stream_id}`, {
      state: { nameOfDeceased: row.name_of_deceased },
    });
  };

  const handleRowClick = (params: GridRowParams<MemoryBookStreamInfo>) => {
    goToStream(params.row);
  };

  const [selectedDeceased, setSelectedDeceased] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStreamForAssign, setSelectedStreamForAssign] =
    useState<MemoryBookStreamInfo | null>(null);
  const [openAssignModal, setOpenAssignModal] = useState(false);

  const handleOpenModal = (name: string) => {
    setSelectedDeceased(name);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDeceased(null);
  };

  const handleOpenAssignModal = (row: MemoryBookStreamInfo) => {
    setSelectedStreamForAssign(row);
    setOpenAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setOpenAssignModal(false);
    setSelectedStreamForAssign(null);
  };

  const columns: GridColDef<MemoryBookStreamInfo>[] = [
    { field: "stream_id", headerName: "Stream ID", width: 80 },
    {
      field: "stream_date",
      headerName: "Date of Stream",
      flex: 1,
      renderCell: (params) => {
        const dateObj = new Date(params.row.stream_date);
        const dayWeekday = dateObj
          .toLocaleDateString("en-GB", { weekday: "long", day: "numeric" })
          .replace(",", "")
          .toUpperCase();
        const monthYear = dateObj
          .toLocaleDateString("en-GB", { month: "long", year: "numeric" })
          .toUpperCase();

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              lineHeight: 1.3,
              gap: 0.5,
            }}
          >
            <span style={{ fontWeight: 600 }}>{dayWeekday}</span>
            <span style={{ fontSize: "0.78rem", opacity: 0.7 }}>{monthYear}</span>
          </Box>
        );
      },
    },
    { field: "name_of_deceased", headerName: "Name of Deceased", flex: 1 },
    {
      field: "comments",
      headerName: "Online Comments",
      flex: 1,
      renderCell: (params) => {
        const { approved_comments, total_comments } = params.row;
        return (
          <TableRowCommentsCounts
            approvedComments={approved_comments}
            allComments={total_comments}
          />
        );
      },
    },
    {
      field: "scanned_comments",
      headerName: "Collected Comments",
      flex: 1,
      renderCell: (params) => {
        const doneComments = params.row.done_comments || 0;
        const physicalComments = params.row.physical_comments || 0;
        return (
          <TableRowScannedComments
            doneComments={doneComments}
            physicalComments={physicalComments}
          />
        );
      },
    },
    {
      field: "book_status",
      headerName: "Book Status",
      flex: 1,
      renderCell: (params) => (
        <MemoryBookStatusChip
          deliveredStatus={params.row.is_delivered}
          isBookAvailable={params.row.is_memory_book_available}
        />
      ),
    },
    {
      field: "canva_link",
      headerName: "Canva Link",
      flex: 1,
      renderCell: (params) => (
        <CanvaLinkButton canvaLink={params.row.canva_memory_book_link} />
      ),
    },
    {
      field: "last_updated_by",
      headerName: "Last Updated by",
      flex: 1,
      renderCell: (params) => {
        const isMemoryBookAvailable = params.row.is_memory_book_available;
        const lastUpdatedBy = params.row.last_updated_by;

        const determinLastUpdateName = () => {
          if (!isMemoryBookAvailable) return "Not available";
          if (isMemoryBookAvailable && !lastUpdatedBy) return "Not updated yet";
          if (lastUpdatedBy.first_name && lastUpdatedBy.last_name) {
            return `${lastUpdatedBy.first_name.charAt(0)} ${lastUpdatedBy.last_name}`;
          }
          return lastUpdatedBy.username;
        };

        return <LastUpdatedByAvatar name={determinLastUpdateName()} />;
      },
    },
    {
      field: "assignee",
      headerName: "Assignee",
      flex: 1,
      renderCell: (params) => {
        const assignees = params.row.assignees;
        if (assignees.length === 0) return <NoAssignees size={25} />;
        return <TableRowAssignees assignees={assignees} />;
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Download Thank you graphic">
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(params.row.name_of_deceased);
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Assign memory book">
            <IconButton
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenAssignModal(params.row);
              }}
            >
              <Assignment />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isMobile) {
    return (
      <Box sx={{ bgcolor: colors.primary[900], p: 1.5, borderRadius: 2 }}>
        {isLoading ? (
          [...Array(4)].map((_, i) => (
            <Card key={i} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, p: 2 }}>
              <Skeleton variant="text" width="55%" height={28} />
              <Skeleton variant="text" width="35%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
            </Card>
          ))
        ) : tableViewStreams.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 4, opacity: 0.7 }}>
            No streams found.
          </Typography>
        ) : (
          <>
            {tableViewStreams.map((row) => (
              <MobileView
                key={row.stream_id}
                row={row}
                onRowClick={goToStream}
                onDownloadClick={handleOpenModal}
                onAssignClick={handleOpenAssignModal}
              />
            ))}
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 1 }}>
              <Pagination
                count={Math.max(1, Math.ceil(rowCount / paginationModel.pageSize))}
                page={paginationModel.page + 1}
                onChange={(_, page) =>
                  handlePaginationModelChange({ ...paginationModel, page: page - 1 })
                }
                color="primary"
                size="small"
              />
            </Box>
          </>
        )}

        <DownloadGraphicModal
          open={openModal}
          onClose={handleCloseModal}
          deceasedName={selectedDeceased || ""}
          backgroundImage={memorygraphicpattern}
          logoImage={muzukurulogo}
        />
        <AssignMemoryBookModal
          streamId={selectedStreamForAssign?.stream_id ?? 0}
          open={openAssignModal}
          onClose={handleCloseAssignModal}
          deceasedName={selectedStreamForAssign?.name_of_deceased || ""}
          onAssignSuccess={onAssignSuccess}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: colors.primary[900], padding: 1, borderRadius: 2 }}>
      <DataGrid
        rows={tableViewStreams}
        getRowId={(row) => row.stream_id}
        columns={columns}
        loading={isLoading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        onRowClick={handleRowClick}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        sx={{
          zIndex: 1,
          "& .MuiDataGrid-row": {
            backgroundColor: colors.primary[900],
          },
          cursor: "pointer",
        }}
      />

      <DownloadGraphicModal
        open={openModal}
        onClose={handleCloseModal}
        deceasedName={selectedDeceased || ""}
        backgroundImage={memorygraphicpattern}
        logoImage={muzukurulogo}
      />
      <AssignMemoryBookModal
        streamId={selectedStreamForAssign?.stream_id ?? 0}
        open={openAssignModal}
        onClose={handleCloseAssignModal}
        deceasedName={selectedStreamForAssign?.name_of_deceased || ""}
        onAssignSuccess={onAssignSuccess}
      />
    </Box>
  );
};

TableView.propTypes = {
  tableViewStreams: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  paginationModel: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }).isRequired,
  rowCount: PropTypes.number.isRequired,
  handlePaginationModelChange: PropTypes.func.isRequired,
  onAssignSuccess: PropTypes.func.isRequired,
};